// Noyau — ENSEMBLES de réels : intervalles (ouverts / fermés, ±∞), ensembles finis, ∅, ℝ, et les opérations
// ∪ (union), ∩ (intersection), \ (privé de). Calcul exact (rationnels) ; aucune notion propre à un exercice.
//
// Un ensemble = liste triée de morceaux disjoints { lo, hi, loIn, hiIn } (lo / hi : rationnel, ou ±Infinity).
// Un point isolé {3} est le morceau [3 ; 3].
//
// parseSet(texte, noms) → { set, issues } ; issues : 'infinityBracket' (crochet fermé du côté de l'infini),
// 'order' (borne de gauche plus grande que celle de droite), 'names' (l'écriture contient encore un nom d'ensemble).

import { num, Q } from './ast.js';

const INF = Infinity;
const cmpB = (a, b) =>
  a === b ? 0 : a === -INF || b === INF ? -1 : a === INF || b === -INF ? 1 : Q.cmp(a, b);
const eqB = (a, b) => cmpB(a, b) === 0;

// ── Lecture ───────────────────────────────────────────────────────────────
function lex(src) {
  const out = [];
  let i = 0;
  const s = String(src).replace(/\s+/g, ' ');
  while (i < s.length) {
    const c = s[i];
    if (c === ' ') {
      i++;
      continue;
    }
    const inf = /^([+−-])\s?∞/.exec(s.slice(i));
    if (inf) {
      out.push({ k: 'inf', v: inf[1] === '+' ? INF : -INF });
      i += inf[0].length;
      continue;
    }
    if (c === '∞') {
      out.push({ k: 'inf', v: INF });
      i++;
      continue;
    }
    const n = /^([−-]\s?)?(\d+(?:[,.]\d+)?)(?:\s?\/\s?(\d+))?/.exec(s.slice(i));
    if (n && n[2]) {
      // un « − » collé à un nombre n'est un signe que s'il ne suit pas une valeur (pas de soustraction ici)
      const [whole, sign, body, den] = n;
      const [ip, fp = ''] = body.replace('.', ',').split(',');
      let v = num(Number(ip + fp), 10 ** fp.length);
      if (den) v = Q.div(v, num(Number(den)));
      if (sign) v = Q.neg(v);
      out.push({ k: 'num', v });
      i += whole.length;
      continue;
    }
    if ('[]{};∪∩\\()∅ℝ'.includes(c)) {
      out.push({ k: c });
      i++;
      continue;
    }
    if (/\p{L}/u.test(c)) {
      let j = i + 1;
      while (j < s.length && /[\p{L}\p{M}₀-₉']/u.test(s[j])) j++;
      out.push({ k: 'name', v: s.slice(i, j) });
      i = j;
      continue;
    }
    throw new Error(`Je ne connais pas le signe « ${c} » dans un ensemble.`);
  }
  return out;
}

export function parseSet(src, names = {}) {
  const t = lex(src);
  let i = 0;
  const issues = new Set();
  const peek = () => t[i],
    next = () => t[i++];
  const bound = () => {
    const x = next();
    if (x?.k === 'num') return x.v;
    if (x?.k === 'inf') return x.v;
    throw new Error('Il manque une borne (un nombre, −∞ ou +∞).');
  };
  function term() {
    const x = peek();
    if (!x) throw new Error('Ensemble incomplet.');
    if (x.k === '∅') {
      next();
      return [];
    }
    if (x.k === 'ℝ') {
      next();
      return [{ lo: -INF, hi: INF, loIn: false, hiIn: false }];
    }
    if (x.k === 'name') {
      next();
      if (!names[x.v]) throw new Error(`Ensemble « ${x.v} » inconnu.`);
      issues.add('names');
      return names[x.v];
    }
    if (x.k === '(') {
      next();
      const e = expr();
      if (next()?.k !== ')') throw new Error('Une parenthèse n’est pas fermée.');
      return e;
    }
    if (x.k === '{') {
      next();
      const pts = [];
      if (peek()?.k === '}') {
        next();
        return [];
      }
      for (;;) {
        const b = bound();
        if (b === INF || b === -INF) throw new Error('L’infini n’est pas un nombre de l’ensemble.');
        pts.push(b);
        const s = next();
        if (s?.k === '}') break;
        if (s?.k !== ';') throw new Error('Sépare les nombres par « ; » et ferme l’accolade.');
      }
      return normalize(pts.map((p) => ({ lo: p, hi: p, loIn: true, hiIn: true })));
    }
    if (x.k === '[' || x.k === ']') {
      next();
      const loIn = x.k === '[';
      const lo = bound();
      if (next()?.k !== ';') throw new Error('Dans un intervalle, sépare les bornes par « ; ».');
      const hi = bound();
      const c = next();
      if (c?.k !== '[' && c?.k !== ']') throw new Error('Ferme l’intervalle par un crochet.');
      const hiIn = c.k === ']';
      if ((lo === -INF && loIn) || (hi === INF && hiIn) || lo === INF || hi === -INF)
        issues.add('infinityBracket');
      if (cmpB(lo, hi) > 0) {
        issues.add('order');
        return [];
      }
      return normalize([{ lo, hi, loIn: loIn && lo !== -INF, hiIn: hiIn && hi !== INF }]);
    }
    throw new Error('Écris un intervalle, un ensemble entre accolades, ∅ ou ℝ.');
  }
  function expr() {
    let a = term();
    for (;;) {
      const op = peek();
      if (op?.k === '∪') {
        next();
        a = union(a, term());
      } else if (op?.k === '∩') {
        next();
        a = inter(a, term());
      } else if (op?.k === '\\') {
        next();
        a = minus(a, term());
      } else break;
    }
    return a;
  }
  const set = expr();
  if (i < t.length) throw new Error('L’écriture de l’ensemble ne se termine pas correctement.');
  return { set, issues: [...issues] };
}

// ── Opérations ────────────────────────────────────────────────────────────
function emptyPiece(p) {
  const c = cmpB(p.lo, p.hi);
  return c > 0 || (c === 0 && !(p.loIn && p.hiIn));
}
export function normalize(pieces) {
  const ps = pieces
    .filter((p) => !emptyPiece(p))
    .sort((a, b) => cmpB(a.lo, b.lo) || (a.loIn === b.loIn ? 0 : a.loIn ? -1 : 1));
  const out = [];
  for (const p of ps) {
    const last = out.at(-1);
    // fusion : chevauchement, ou contact avec au moins une borne fermée
    if (last && (cmpB(p.lo, last.hi) < 0 || (eqB(p.lo, last.hi) && (p.loIn || last.hiIn)))) {
      const c = cmpB(p.hi, last.hi);
      if (c > 0) {
        last.hi = p.hi;
        last.hiIn = p.hiIn;
      } else if (c === 0) last.hiIn = last.hiIn || p.hiIn;
      if (eqB(p.lo, last.lo)) last.loIn = last.loIn || p.loIn;
    } else out.push({ ...p });
  }
  return out;
}
export const union = (a, b) => normalize([...a, ...b]);
export function inter(a, b) {
  const out = [];
  for (const p of a)
    for (const q of b) {
      const lc = cmpB(p.lo, q.lo),
        hc = cmpB(p.hi, q.hi);
      const lo = lc >= 0 ? p.lo : q.lo,
        loIn = lc > 0 ? p.loIn : lc < 0 ? q.loIn : p.loIn && q.loIn;
      const hi = hc <= 0 ? p.hi : q.hi,
        hiIn = hc < 0 ? p.hiIn : hc > 0 ? q.hiIn : p.hiIn && q.hiIn;
      out.push({ lo, hi, loIn, hiIn });
    }
  return normalize(out);
}
export function complement(a) {
  const out = [];
  let lo = -INF,
    loIn = false;
  for (const p of normalize(a)) {
    out.push({ lo, hi: p.lo, loIn, hiIn: !p.loIn && p.lo !== -INF });
    lo = p.hi;
    loIn = !p.hiIn && p.hi !== INF;
  }
  out.push({ lo, hi: INF, loIn, hiIn: false });
  return normalize(out);
}
export const minus = (a, b) => inter(a, complement(b));
export const sameSet = (a, b) => {
  const x = normalize(a),
    y = normalize(b);
  return (
    x.length === y.length &&
    x.every(
      (p, i) =>
        eqB(p.lo, y[i].lo) && eqB(p.hi, y[i].hi) && p.loIn === y[i].loIn && p.hiIn === y[i].hiIn,
    )
  );
};
// mêmes bornes, crochets différents : l'erreur de crochet
export const sameClosure = (a, b) => {
  const cl = (s) => normalize(s.map((p) => ({ ...p, loIn: p.lo !== -INF, hiIn: p.hi !== INF })));
  return sameSet(cl(a), cl(b));
};
export const contains = (a, x) =>
  a.some(
    (p) =>
      (cmpB(p.lo, x) < 0 || (eqB(p.lo, x) && p.loIn)) &&
      (cmpB(x, p.hi) < 0 || (eqB(p.hi, x) && p.hiIn)),
  );

// ── Écriture ──────────────────────────────────────────────────────────────
const bText = (b) =>
  b === INF
    ? '+∞'
    : b === -INF
      ? '−∞'
      : (b.p < 0 ? '−' : '') + (b.q === 1 ? String(Math.abs(b.p)) : `${Math.abs(b.p)}/${b.q}`);
export function setText(a) {
  const s = normalize(a);
  if (!s.length) return '∅';
  if (s.length === 1 && s[0].lo === -INF && s[0].hi === INF) return 'ℝ';
  // que des points : un ensemble fini
  if (s.every((p) => eqB(p.lo, p.hi))) return `{${s.map((p) => bText(p.lo)).join(' ; ')}}`;
  return s
    .map((p) =>
      eqB(p.lo, p.hi)
        ? `{${bText(p.lo)}}`
        : `${p.loIn ? '[' : ']'}${bText(p.lo)} ; ${bText(p.hi)}${p.hiIn ? ']' : '['}`,
    )
    .join(' ∪ ');
}

// Relation simple en x → ensemble des solutions : « x ≥ 3 », « x < −1 », « −2 ≤ x < 5 »
export function relationSet(src, x = 'x') {
  const s = String(src).replace(/\s+/g, '');
  const n = '([−-]?\\d+(?:[,.]\\d+)?)';
  const v = (t) => parseSet(`{${t}}`).set[0].lo;
  let m = new RegExp(`^${x}(≤|<|≥|>)${n}$`).exec(s);
  if (m) {
    const a = v(m[2]);
    return m[1] === '≤'
      ? [{ lo: -INF, hi: a, loIn: false, hiIn: true }]
      : m[1] === '<'
        ? [{ lo: -INF, hi: a, loIn: false, hiIn: false }]
        : m[1] === '≥'
          ? [{ lo: a, hi: INF, loIn: true, hiIn: false }]
          : [{ lo: a, hi: INF, loIn: false, hiIn: false }];
  }
  m = new RegExp(`^${n}(≤|<)${x}(≤|<)${n}$`).exec(s);
  if (m) return normalize([{ lo: v(m[1]), hi: v(m[4]), loIn: m[2] === '≤', hiIn: m[3] === '≤' }]);
  throw new Error(`relation « ${src} » non prise en charge (x ≥ a, x < b, a ≤ x < b…)`);
}
